import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput } from '@/components/design-system/atoms/text/text-input';
import { Button } from '@/components/design-system/atoms/button';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { SelectChangeEvent } from '@mui/material';
import {
  AdminSideNavigation,
  AdminRoutes,
} from '@/features/admin/navigation/containers/admin-side-navigation';
import ContractWizardTimeline from '../contract-wizard-timeline/ContractWizardTimeline';
import { DropDown } from '@/components/design-system/molecules/dropdown';
import { PaginatedColocApiConnectedDropDown } from '@/features/shared/feature-paginated-coloc-api-connected-dropdown';
import { House as BaseHouse } from '@/types/admin';
import { useGetHousesInfinite } from '@/features/shared/hooks/use-get-houses-infinite';

// Extend House type to include rooms array
interface House extends Omit<BaseHouse, 'rooms'> {
  rooms: {
    rooms_count: number;
    available_rooms_count: number;
    rooms: [number, number][]; // [room_number, room_id]
  };
}

// Type for API response house
type APIHouse = BaseHouse & {
  rooms: {
    rooms_count: number;
    available_rooms_count: number;
  };
};

interface PickRoomFormProps {
  isOpen: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onSubmit: (formData: RoomFormData) => void;
  initialData?: RoomFormData;
}

interface RoomFormData {
  houseId: string;
  roomId: string;
  accessCode?: string;
}

const PickRoomForm: React.FC<PickRoomFormProps> = ({
  isOpen,
  onClose,
  onPrevious,
  onSubmit,
  initialData,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<RoomFormData>(
    initialData || {
      houseId: '',
      roomId: '',
      accessCode: '',
    }
  );

  // State for house search and selection
  const [selectedHouse, setSelectedHouse] = useState<House | null>(null);
  const [housesQuery, setHousesQuery] = useState('');

  // Load initial house data if available
  const { fetch } = useGetHousesInfinite({
    searchQuery: '',
  })!;

  useEffect(() => {
    if (initialData?.houseId && fetch.data?.pages?.[0]?.data?.data) {
      const apiHouse = fetch.data.pages[0].data.data.find(
        (house: APIHouse) => house.id.toString() === initialData.houseId
      );
      if (apiHouse) {
        // Transform API house to our internal House type
        const house: House = {
          ...apiHouse,
          rooms: {
            ...apiHouse.rooms,
            rooms: [],
          },
        };
        setSelectedHouse(house);
      }
    }
  }, [initialData, fetch.data]);

  useEffect(() => {
    if (selectedHouse) {
      setFormData((prev) => ({
        ...prev,
        houseId: selectedHouse.id.toString(),
        roomId: prev.roomId || '',
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        houseId: '',
        roomId: '',
      }));
    }
  }, [selectedHouse]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoomChange = (e: SelectChangeEvent<string>) => {
    setFormData((prev) => ({ ...prev, roomId: e.target.value as string }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Cast components to any to avoid TypeScript errors
  const IconSaxIconAny = IconSaxIcon as any;
  const ButtonAny = Button as any;
  const TextInputAny = TextInput as any;
  const AdminSideNavigationAny = AdminSideNavigation as any;
  const ContractWizardTimelineAny = ContractWizardTimeline as any;
  const PaginatedColocApiConnectedDropDownAny = PaginatedColocApiConnectedDropDown as any;
  const DropDownAny = DropDown as any;

  if (!isOpen) return null;

  const colocName = sessionStorage.getItem('colocFirstName') || 'Coloc-first-name';
  const roomOptions = selectedHouse?.rooms.rooms || [];

  return (
    <div className="flex h-screen w-screen">
      {/* Sidebar */}
      <div className="flex-shrink-0">
        <AdminSideNavigationAny tabToSet={AdminRoutes.Contracts} />
      </div>

      {/* Main content */}
      <div className="flex h-full flex-1 flex-col bg-gray-50 p-6">
        {/* Breadcrumb and page title */}
        <div className="border-b border-gray-200">
          <div className="flex items-center text-[14px] font-medium">
            <span className="text-text-primaryLight">
              {t('js.features.admin.contracts.pick_room_form.page_name')}
            </span>
            <IconSaxIconAny name="arrow-right-1" size={16} className="mx-2" />
            <span className="text-N900">
              {t('js.features.admin.contracts.pick_room_form.select_room')}
            </span>
          </div>
          <h1 className="mt-2 text-[25px] font-bold leading-loose text-text-primaryHeavy">
            {t('js.features.admin.contracts.pick_room_form.create_contract')}
          </h1>
        </div>

        {/* Wizard timeline */}
        <div className="rounded-lg border-b border-gray-200 bg-white px-8 py-4">
          <ContractWizardTimelineAny currentStep={2} />
        </div>

        {/* Form content */}
        <div className="flex-1 overflow-auto py-6">
          <form onSubmit={handleSubmit}>
            <div className="mb-8 rounded-lg bg-white p-6 shadow-sm">
              <div className="mb-2">
                <h2
                  style={{ fontSize: '18px' }}
                  className="font-bold leading-relaxed text-text-primaryHeavy"
                >
                  {t('js.features.admin.contracts.pick_room_form.moving_to_question', {
                    name: colocName,
                  })}
                </h2>
                <p className="text-[14px] font-bold text-N900">
                  {t('js.features.admin.contracts.pick_room_form.give_home', {
                    name: colocName,
                  })}
                </p>
              </div>

              <p className="mb-6 text-[14px] text-text-primaryLight">
                {t('js.features.admin.contracts.pick_room_form.select_colocation')}
              </p>
              <h3 className="mb-4 text-[16px] font-medium text-gray-900">
                {t('js.features.admin.contracts.pick_room_form.colocation_details')}
              </h3>

              <div className="mb-4 grid w-[500px] grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[12px] font-medium text-N900">
                    {t('js.features.admin.contracts.pick_room_form.house')}
                  </label>
                  <div className="w-full">
                    <PaginatedColocApiConnectedDropDownAny<House>
                      placeholder={t('js.features.admin.contracts.pick_room_form.select_house')}
                      className="w-full"
                      showLabel={false}
                      value={selectedHouse}
                      onChange={setSelectedHouse}
                      {...(selectedHouse && {
                        displayValue: selectedHouse.name,
                      })}
                      onTextInputChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setHousesQuery(event.target.value)
                      }
                      dataSource={
                        useGetHousesInfinite({
                          searchQuery: housesQuery,
                        })!
                      }
                      itemRenderer={(itemProps: House, ref: React.Ref<HTMLDivElement>) => {
                        return (
                          <DropDownAny.DropDownOptionDefaultContent
                            ref={ref}
                            className="font-['sofia-pro'] text-[12px] font-normal leading-[21px] text-[#212529]"
                          >
                            {itemProps.name}
                          </DropDownAny.DropDownOptionDefaultContent>
                        );
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-[12px] font-medium text-N900">
                    {t('js.features.admin.contracts.pick_room_form.room')}
                  </label>
                  <div className="w-full">
                    <DropDownAny
                      placeholder={
                        t(
                          'js.features.admin.contracts.pick_room_form.select_room_placeholder'
                        ) as string
                      }
                      className="w-full"
                      showLabel={false}
                      value={formData.roomId}
                      onChange={(selectedRoomId: string) =>
                        setFormData((prev) => ({ ...prev, roomId: selectedRoomId.toString() }))
                      }
                      disabled={!selectedHouse || roomOptions.length === 0}
                      displayValue={
                        roomOptions.find((room) => room[1].toString() === formData.roomId)
                          ? (t('js.features.admin.contracts.pick_room_form.room_number', {
                              number: roomOptions.find(
                                (room) => room[1].toString() === formData.roomId
                              )?.[0],
                            }) as string)
                          : ''
                      }
                    >
                      <DropDownAny.DropDownOptions>
                        {roomOptions.map((room) => (
                          <DropDownAny.DropDownOption key={room[1]} value={room[1].toString()}>
                            <DropDownAny.DropDownOptionDefaultContent>
                              {
                                t('js.features.admin.contracts.pick_room_form.room_number', {
                                  number: room[0],
                                }) as string
                              }
                            </DropDownAny.DropDownOptionDefaultContent>
                          </DropDownAny.DropDownOption>
                        ))}
                      </DropDownAny.DropDownOptions>
                    </DropDownAny>
                  </div>
                </div>
              </div>

              {/* Access code */}
              <div className="mb-4 w-[250px]">
                <label className="mb-1 flex justify-between text-[12px] font-medium text-N900">
                  {t('js.features.admin.contracts.pick_room_form.access_code')}
                  <span className="mt-1 text-[12px] text-text-primaryLight">
                    {t('js.features.admin.contracts.pick_room_form.optional')}
                  </span>
                </label>
                <TextInputAny
                  name="accessCode"
                  value={formData.accessCode}
                  onChange={handleInputChange}
                  placeholder={t('js.features.admin.contracts.pick_room_form.enter_access_code')}
                  className="w-full"
                  label={t('js.features.admin.contracts.pick_room_form.access_code')}
                  showLabel={false}
                />
              </div>
              <div className="mt-4 flex justify-between">
                <button
                  type="button"
                  onClick={onClose}
                  className="mb-5 border-b bg-transparent text-[16px]"
                >
                  {t('js.features.admin.contracts.pick_room_form.cancel')}
                </button>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={onPrevious}
                    style={{ backgroundColor: '#F3F4F5' }}
                    className="flex h-[48px] w-[128px] items-center justify-center gap-1 rounded-[8px] px-4 py-[12px] font-sans text-[16px] font-medium leading-[20px] text-[#212529] hover:bg-gray-200 disabled:bg-E50 disabled:text-E75"
                  >
                    {t('js.features.admin.contracts.pick_room_form.previous')}
                  </button>
                  <button
                    type="submit"
                    className="flex h-[48px] w-[128px] items-center justify-center gap-1 rounded-[8px] bg-R300 px-4 py-[12px] font-sans text-[16px] font-medium leading-[20px] text-[#212529] hover:bg-R400 disabled:bg-E50 disabled:text-E75"
                    disabled={!formData.roomId || !formData.houseId}
                  >
                    {t('js.features.admin.contracts.pick_room_form.next')}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PickRoomForm;
